import { useSyncExternalStore } from "react";
import { devToolsEnabled } from "./dev-tools-flag";

/**
 * The prize drop's title chip words on the button, set in the dev frame
 * (Sebastian, 2026-10-21: "write in there 'Prize Drops' — this label can be
 * changed in dev tools"). The layout itself is no longer a choice: the label
 * layout is the only one (2026-10-22: "replace the layout option in dev tools
 * for states"; the total-led slab is deleted). Remembered across reloads
 * where the dev tools are on (`devToolsEnabled`); everywhere else the words
 * are the default.
 */

export interface PrizeDropLayoutState {
  /** The title chip's words; empty: the translated "Prize Drops". */
  label: string;
}

const KEY = "uniboIgwDevPrizeDropLayout";
const DEFAULT: PrizeDropLayoutState = { label: "" };
let state: PrizeDropLayoutState = devToolsEnabled ? read() : DEFAULT;
const listeners = new Set<() => void>();

function read(): PrizeDropLayoutState {
  try {
    const saved = JSON.parse(window.localStorage.getItem(KEY) ?? "null");
    // A saved layout from before is ignored; the words stay.
    return { label: String(saved?.label ?? "") };
  } catch {
    return DEFAULT;
  }
}

export const prizeDropLayout = {
  get: (): PrizeDropLayoutState => state,
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  set(next: Partial<PrizeDropLayoutState>): void {
    state = { ...state, ...next };
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* private mode: the pick just doesn't stick */
    }
    listeners.forEach((l) => l());
  },
};

/** The title words in force (re-renders on a change in the dev frame). */
export function usePrizeDropLayout(): PrizeDropLayoutState {
  return useSyncExternalStore(prizeDropLayout.subscribe, prizeDropLayout.get);
}
